import {
} from "@modules/plaintes/plaintes.model.ts";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { MdOutlineViewWeek } from "react-icons/md";
import { AgeGroupMapping, ColumnTableInternConfig, DataInternComplaintList } from "../dashbord.model.ts";
import SupportTypeBody from "./supportTypeBody.tsx";
import { useAppDispatch, useAppSelector } from "@core/store/hook.ts";
import { getFileInternAgent } from "@core/actions/dashboardInternAction.ts";
import { FetchStatusType } from "@core/modeles/model.ts";


interface ComplaintReportTableByCategoryProps {
  onShowMap: () => void;
  title: string;
  dataTableList?: DataInternComplaintList[];
  typeProfil: string
}

const ageGroupMapping = AgeGroupMapping

const ComplaintReportTableByCategory: React.FC<ComplaintReportTableByCategoryProps> = ({ 
    onShowMap,
    title,
    dataTableList,
    typeProfil
  }) => {
    const dispatch = useAppDispatch();
    const { loadingExport } = useAppSelector((state) => state.dashboard);
    // Conversion de l'age brut en groupe d'âge
    const formatAgeGroup = (rawAgeGroup?: string): string => {
      const found = ageGroupMapping.find(item => item.key === rawAgeGroup);
      return found ? found.label : rawAgeGroup || "";
    };

    // Capitalisation de la première lettre d'une chaîne
    const capitalizeFirstLetter = (string: string) => {
      return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
    }

    // Conversion du sexe
    const formatSex = (rawSex?: string): string => {
      if (rawSex === "MALE") {
        return "Garçon";
      } else if (rawSex === "FEMALE") {
        return "Fille";
      } else {
        return rawSex || "";
      }
    }

    // Configuration des colonnes en fonction du type de profil
    const columnConfigs: Record<string, ColumnTableInternConfig[]> = {
      "VBG": [
        { 
          field: "ageGroup",
          header: "Age (en année)",
          body: (row) => formatAgeGroup(row.ageGroup),
        },
        {
          field: "nature",
          header: "Type de violence"
        },
        {
          field: "supportType",
          header: "Statut de l'accompagnement",
          body: (row) => row.supportType ? <SupportTypeBody supportType={row.supportType}/> : ""
        },
        {
          field: "count",
          header: "Nombre de plaintes",
        },
      ],
      "PE": [
        {
          field: "ageGroup",
          header: "Age (en année)",
          body: (row) => formatAgeGroup(row.ageGroup),
        },
        {
          field: "gender",
          header: "Sexe",
          body: (row) => formatSex(row.gender)
        },
        {
          field: "count",
          header: "Nombre de plaintes",
        },
        {
          field: "region",
          header: "Région",
          body: (row) => row.region ? capitalizeFirstLetter(row.region) : ""
        },
      ],
      "PH/PA": [
        {
          field: "type",
          header: "Type de personne"
        },
        {
          field: "nature",
          header: "Catégorie de plainte"
        },
        {
          field: "supportType",
          header: "Type d'accompagnement offert"
        },
        {
          field: "count",
          header: "Nombre de plaintes",
        }
      ],
      "MOCIP": [
        {
          field: "nature",
          header: "Type de plaintes",
        },
        {
          field: "region",
          header: "Région",
          body: (row) => row.region ? capitalizeFirstLetter(row.region) : ""
        },
        {
          field: "count",
          header: "Nombre de plaintes",
        }
      ],
      "PSRC": [
        {
          field: "region",
          header: "Région",
          body: (row) => row.region ? capitalizeFirstLetter(row.region) : ""
        },
        {
          field: "district",
          header: "District",
          body: (row) => row.district ? capitalizeFirstLetter(row.district) : "",
        },
        {
          field: "count",
          header: "Plainte reçue",
        },
        {
          field: "resolvedPourcentage",
          header: "Taux de résolution",
          body: (row) => `${row.resolvedPourcentage}%`,
        },
        {
          field: "pendingCount",
          header: "Plainte en cours",
        },
        {
          field: "satisfiedPourcentage",
          header: "Taux de satisfaction",
        },
      ]
    };

    const columnsToShow = columnConfigs[typeProfil];

    const handleExportClick = async() => {
      const resultAction = await dispatch(getFileInternAgent());
      if (getFileInternAgent.fulfilled.match(resultAction)) {
        const blob = resultAction.payload as Blob;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'complaints.xlsx';
        link.click();
        window.URL.revokeObjectURL(url);
      }
    };

    return (
      <div className="bg-gray-50 rounded-lg shadow-md w-full my-6 md:my-0">
        <div className="p-4 flex items-center text-md font-semibold text-gray-600 border-b border-gray-300 pb-2">
          <span>{title}</span>
          <div className="ml-auto gap-2 md:flex">
            <Button
              label=""
              className="p-3 w-[150px] h-[25px] rounded-md border border-[#086d33] flex "
              onClick={handleExportClick}
              disabled={loadingExport === FetchStatusType.LOADING}
            >
              <span style={{ color: "var(--primary-color)", fontSize: "12px" }}>
               {loadingExport === FetchStatusType.LOADING ?  "Telechargement" : "Exporter"}
              </span>
              {loadingExport === FetchStatusType.LOADING ? 
                <i className="pi pi-spin pi-spinner ml-auto" />  : 
                <i className="fas fa-upload ml-auto" style={{ color: "var(--primary-color)" }}></i>
              }
              
              
              {/* Icône de téléchargement */}
            </Button>

            <Button
              label=""
              className="p-3 w-[150px] h-[25px] rounded-md border border-[#086d33] flex "
              onClick={onShowMap}
            >
              <span style={{ color: "var(--primary-color)", fontSize: "12px" }}>
                Voir sur la carte
              </span>
              <MdOutlineViewWeek
                color="var(--primary-color)"
                className="ml-auto"
              />
            </Button>
          </div>
        </div>
        <div className="m-6 overflow-x-auto" style={{ maxWidth: "100%" }}>
          <DataTable value={dataTableList} removableSort stripedRows size="small">
              {columnsToShow.map((col) => (
                <Column
                  key={col.field}
                  field={col.field}
                  header={col.header}
                  style={col.style}
                  body={col.body}
                  sortable={col.sortable}
                  align={col.field === "count" ? "right" : "left"}
                />
              ))}
          </DataTable>
        </div>
      </div>
    );
};

export default ComplaintReportTableByCategory;
